import React, { useState } from "react";
import { Formik, Form, Field } from "formik";
import { runQuery } from "../hooks/use-query";

const CREATE_QUERY = `
  mutation ($objects: [recipes_insert_input!]!) {
    insert_recipes(objects: $objects) {
      returning {
        id
        imageUrl
        ingredients
        instructions
        name
        recipeUrl
      }
    }
  }
`;

const UPDATE_QUERY = `
  mutation ($id: Int, $changes: recipes_set_input) {
    update_recipes(where: {id: {_eq: $id}}, _set: $changes) {
      returning {
        id
        imageUrl
        ingredients
        instructions
        name
        recipeUrl
      }
    }
  }
`;

const DELETE_QUERY = `
  mutation ($id: Int) {
    delete_recipes(where: {id: {_eq: $id}}) {
      returning {
        id
      }
    }
  }
`;

export default function RecipeForm({
  recipe,
  didSave = () => {},
  didDelete = () => {}
}) {
  let [isDeleting, setIsDeleting] = useState(false);

  function handleSubmit(changedRecipe) {
    let promise;

    if (changedRecipe.id) {
      promise = runQuery(UPDATE_QUERY, {
        variables: { id: changedRecipe.id, changes: changedRecipe }
      }).then(json => json.update_recipes.returning[0]);
    } else {
      promise = runQuery(CREATE_QUERY, {
        variables: { objects: [changedRecipe] }
      }).then(json => json.insert_recipes.returning[0]);
    }

    return promise;
  }

  function handleDelete(event) {
    event.preventDefault();
    setIsDeleting(true);

    runQuery(DELETE_QUERY, {
      variables: { id: recipe.id }
    }).then(() => {
      setIsDeleting(false);
      didDelete();
    });
  }

  let initialValues = { ...recipe };
  // Convert null values into empty strings for Formik
  Object.keys(initialValues).forEach(key => {
    if (initialValues[key] === null) {
      initialValues[key] = "";
    }
  });

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(changedRecipe, { setSubmitting }) => {
        handleSubmit(changedRecipe).then(savedRecipe => {
          setSubmitting(false);
          didSave(savedRecipe);
        });
      }}
    >
      {({ isSubmitting }) => (
        <Form>
          <div className="mt-6">
            <label
              htmlFor="name"
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              Name
            </label>
            <Field
              name="name"
              autoFocus
              className="form-input w-full"
              placeholder="Chicken and Broccoli"
            />
          </div>

          <div className="mt-6">
            <label
              htmlFor="imageUrl"
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              Image URL
            </label>
            <Field
              name="imageUrl"
              className="form-input w-full"
              placeholder="https://www.wellplated.com/wp-content/uploads/2014/04/Cheesy-Chicken-Broccoli-Rice-Casserole-2.11.jpg"
            />
          </div>

          <div className="mt-6">
            <label
              htmlFor="ingredients"
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              Ingredients
            </label>
            <Field
              name="ingredients"
              component="textarea"
              rows="5"
              className="form-textarea w-full"
              placeholder="Butter and sugar"
            />
          </div>

          <div className="mt-6">
            <label
              htmlFor="instructions"
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              Instructions
            </label>
            <Field
              name="instructions"
              component="textarea"
              rows="5"
              className="form-textarea w-full"
              placeholder="Mix em together!"
            />
          </div>

          <div className="mt-6">
            <label
              htmlFor="recipeUrl"
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              Recipe URL
            </label>
            <Field
              name="recipeUrl"
              className="form-input w-full"
              placeholder="https://www.wellplated.com/chicken-broccoli-rice-casserole/"
            />
          </div>

          <div className="mt-12 flex justify-between">
            {recipe.id ? (
              <button
                type="button"
                onClick={handleDelete}
                disabled={isDeleting}
                className={`py-2 font-medium text-red-500 ${
                  isDeleting ? "opacity-50" : ""
                }`}
              >
                Delete
              </button>
            ) : null}

            <div className="ml-auto">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`py-2 text-blue-500 font-bold ${
                  isSubmitting ? "opacity-50" : ""
                }`}
              >
                Save
              </button>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
}
